import { Button } from '@oysterjs/ui/button';

import type { EmailDraftTemplate } from '~/api/data/email-templates/queries';
import { dateFormatter } from '~/components/table-formatters';
import { InlineNotice } from '~/components/inline-notice';
import { WAIT_FOR_CARA_REASON } from './hooks/use-template-redraft';
import type { DraftTemplateState } from './hooks/use-composer-email-templates';

/**
 * The template, when it changed after the draft was made from it. An archived template isn't
 * offered again, so it never counts as newer.
 */
export const newerTemplateThanDraft = (
  draftTemplate: DraftTemplateState,
  draftTemplateVersion: number | undefined
): EmailDraftTemplate | null => {
  if (draftTemplate.status !== 'loaded' || draftTemplateVersion === undefined) return null;
  const { template } = draftTemplate;
  const isNewer = template.version > draftTemplateVersion && !template.archivedAt;
  return isNewer ? template : null;
};

/** Says the template changed after this draft was made, and offers to redraft with it. */
export const OlderVersionNotice = (props: {
  template: EmailDraftTemplate;
  /** Why the email can't be redrafted right now, or undefined when it can. */
  redraftDisabledReason: string | undefined;
  onRedraft: () => void;
  onKeep: () => void;
}) => {
  const { template } = props;
  // While Cara redrafts, the "Cara is writing" notice above already says why.
  const showsDisabledReason =
    !!props.redraftDisabledReason && props.redraftDisabledReason !== WAIT_FOR_CARA_REASON;
  const updatedOn = dateFormatter.format(new Date(template.updatedAt));
  const updatedBy = template.updatedBy ? `${template.updatedBy.name} updated it` : 'It was updated';
  return (
    <InlineNotice
      className="mb-3"
      tone="info"
      title={`${template.name} changed after this draft was made.`}
      description={`${updatedBy} on ${updatedOn}. This draft keeps the wording it started with.`}
      actions={
        <>
          <Button
            color="white"
            disabled={!!props.redraftDisabledReason}
            title={props.redraftDisabledReason}
            onClick={props.onRedraft}
          >
            Redraft with the latest version
          </Button>
          <Button plain onClick={props.onKeep}>
            Keep this draft
          </Button>
          {showsDisabledReason && (
            <span className="self-center text-xs">{props.redraftDisabledReason}</span>
          )}
        </>
      }
    />
  );
};
